import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getAllRequests,
  updateRequest,
} from "./../../../../services/requestsServices";

const RequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [newDescription, setNewDescription] = useState("");
  const [filterAsso, setFilterAsso] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const data = await getAllRequests();
        setRequests(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des requêtes :", error);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const toggleExpand = (request) => {
    setExpandedRequest(expandedRequest?.id === request.id ? null : request);
    setEditingRequest(null);
  };

  const handleEditClick = (request) => {
    setEditingRequest(request);
    setNewDescription(request.description);
  };

  const handleStatusChange = async (requestId, currentStatus) => {
    const nextStatus =
      currentStatus === "Pending"
        ? "In Progress"
        : currentStatus === "In Progress"
        ? "Completed"
        : "Pending";

    try {
      await updateRequest(requestId, { status: nextStatus });
      setRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === requestId ? { ...req, status: nextStatus } : req
        )
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut :", error);
    }
  };

  const handleUpdateRequest = async () => {
    if (!editingRequest) return;
    try {
      await updateRequest(editingRequest.id, { description: newDescription });
      setRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === editingRequest.id
            ? { ...req, description: newDescription }
            : req
        )
      );
      setEditingRequest(null);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la requête :", error);
    }
  };

  const filteredRequests = requests.filter(
    (req) =>
      (!filterAsso || req.Associations.nom_asso === filterAsso) &&
      (!filterStatus || req.status === filterStatus)
  );

  return (
    <div className="max-w-[1280px] mx-auto">
      <h2 className="text-2xl font-bold mb-4">Résumé des Requêtes</h2>

      {/* Filtres */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Filtrer par association */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Filtrer par association :
          </label>
          <select
            className="w-full p-2 border rounded"
            value={filterAsso}
            onChange={(e) => setFilterAsso(e.target.value)}
          >
            <option value="">Toutes les associations</option>
            {[...new Set(requests.map((req) => req.Associations.nom_asso))].map(
              (asso) => (
                <option key={asso} value={asso}>
                  {asso}
                </option>
              )
            )}
          </select>
        </div>

        {/* Filtrer par statut */}
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            Filtrer par statut :
          </label>
          <select
            className="w-full p-2 border rounded"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Tous les statuts</option>
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p>Chargement des données...</p>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className="border p-4 rounded-lg shadow-md bg-white cursor-pointer transition hover:shadow-lg"
              onClick={() => toggleExpand(request)}
            >
              {/* Ligne d'en-tête */}
              <div className="flex flex-col sm:flex-col sm:justify-between sm:items-left">
                <div className="flex items-center space-x-3">
                  <img
                    src={request.Associations.logo_asso}
                    alt={request.Associations.nom_asso}
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="font-semibold">
                    {request.Associations.nom_asso}
                  </span>
                </div>
                <span className="font-bold">{request.title}</span>

                <div className="flex space-x-2 mt-2 sm:mt-0">
                  {/* Statut */}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(request.id, request.status);
                    }}
                    className={`cursor-pointer px-3 py-1 rounded text-white text-sm ${
                      request.status === "Pending"
                        ? "bg-yellow-500"
                        : request.status === "In Progress"
                        ? "bg-blue-500"
                        : "bg-green-500"
                    }`}
                  >
                    {request.status}
                  </span>

                  {/* Priorité */}
                  <span
                    className={`px-3 py-1 rounded text-white text-sm ${
                      request.priority === "Low"
                        ? "bg-green-400"
                        : request.priority === "Medium"
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                  >
                    {request.priority}
                  </span>
                </div>
              </div>

              {/* Contenu affiché au clic */}
              {expandedRequest?.id === request.id && (
                <div className="mt-4 border-t pt-4">
                  <p
                    className="text-gray-600"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(request.description),
                    }}
                  ></p>

                  {/* Bouton Modifier */}
                  <div className="mt-4 flex justify-end">
                    <button
                      className="bg-blue-500 text-white px-4 py-2 rounded"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(request);
                      }}
                    >
                      Modifier
                    </button>
                  </div>
                </div>
              )}

              {/* Formulaire de modification */}
              {editingRequest?.id === request.id && (
                <div
                  className="mt-4 border-t pt-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold mb-2">
                    Modifier la description
                  </h3>
                  <ReactQuill
                    value={newDescription}
                    onChange={setNewDescription}
                    className="mb-4"
                  />
                  <div className="flex justify-end space-x-2">
                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded"
                      onClick={() => setEditingRequest(null)}
                    >
                      Annuler
                    </button>
                    <button
                      className="bg-blue-500 text-white px-4 py-2 rounded"
                      onClick={handleUpdateRequest}
                    >
                      Sauvegarder
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RequestsList;
